import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getAuditEvents } from "./api/costModelApi.js";
import RightSheet from "../../components/ui-components/RightSheet.jsx";

// Human label + detail line for one audit event on this node.
function describe(ev) {
  const p = ev.payload || {};
  switch (ev.event_type) {
    case "cost_model.node_added":
      return { label: "Created", detail: p.name ? `Created as "${p.name}"${p.code ? ` (${p.code})` : ""}` : "Created" };
    case "cost_model.node_updated": {
      const from = p.from_name ?? ev.before;
      if (from && from !== (p.name ?? ev.after)) return { label: "Renamed", detail: `"${from}" → "${p.name ?? ev.after}"` };
      return { label: "Updated", detail: p.name ? `"${p.name}"` : "Updated" };
    }
    case "cost_model.node_removed":
      return { label: "Removed", detail: p.name ? `Removed "${p.name}"` : "Removed" };
    case "cost_model.node_moved":
      return { label: "Moved", detail: "Reparented in the hierarchy" };
    default:
      return { label: ev.event_type, detail: "" };
  }
}

const actorLabel = (id) => (id ? `User #${id}` : "—");
const whenLabel = (t) => {
  try { return new Date(t).toLocaleString(); } catch { return String(t ?? ""); }
};

// NodeHistoryDrawer shows a single node's change history: its own add/rename/remove events plus
// any moves extracted from the version's edge-change events that reference this node. It uses the
// app's shared RightSheet so it reads identically to the other side drawers.
const NodeHistoryDrawer = ({ open, onClose, node, versionId }) => {
  const nodeId = node?.id != null ? Number.parseInt(node.id, 10) : null;

  const nodeEventsQuery = useQuery({
    queryKey: ["audit", "cost_model_node", nodeId],
    queryFn: () => getAuditEvents("cost_model_node", nodeId),
    enabled: open && nodeId != null,
  });
  const versionEventsQuery = useQuery({
    queryKey: ["audit", "cost_model_version", versionId],
    queryFn: () => getAuditEvents("cost_model_version", versionId),
    enabled: open && versionId != null,
  });

  const moveEvents = (versionEventsQuery.data || [])
    .filter((ev) => ev.event_type === "cost_model.edges_changed")
    .flatMap((ev) =>
      (((ev.payload && ev.payload.changes) || []))
        .filter((c) => c.child_node_id === nodeId && c.kind === "moved")
        .map(() => ({ ...ev, event_type: "cost_model.node_moved" }))
    );

  const events = [...(nodeEventsQuery.data || []), ...moveEvents].sort(
    (a, b) => new Date(b.event_time) - new Date(a.event_time)
  );
  const loading = nodeEventsQuery.isLoading || versionEventsQuery.isLoading;

  return (
    <RightSheet open={open} onClose={onClose} title="History" subtitle={node?.name ?? "node"}>
      {loading && <p className="text-sm text-text-2">Loading…</p>}
      {!loading && events.length === 0 && (
        <p className="text-sm text-text-2">No recorded changes for this node yet.</p>
      )}
      <ul className="space-y-3">
        {events.map((ev, i) => {
          const d = describe(ev);
          return (
            <li key={`${ev.id}-${i}`} className="border-l-2 border-border pl-3">
              <div className="text-sm font-medium text-text">{d.label}</div>
              {d.detail && <div className="text-sm text-text-2">{d.detail}</div>}
              <div className="text-xs text-text-2 mt-0.5">{actorLabel(ev.actor_user_id)} · {whenLabel(ev.event_time)}</div>
            </li>
          );
        })}
      </ul>
    </RightSheet>
  );
};

export default NodeHistoryDrawer;
